import { useCallback, useEffect, useReducer } from "react"

import { cortexGet } from "./cortexFetch"
import defaultReducer from "../../utils/defaultReducer"

const DEFAULT_KB_STORAGE_KEY = 'defaultKnowledgeBase'

// Lists every knowledge base and tabular file available to the enterprise's
// Cortex accountcode - kwikweb never scopes this down (no kbIds/fileIds
// filtering is ever configured), so it always lists everything, matching
// today's kwik-assistant behavior.
const fetchAllPages = async (assistantUrl, accessToken, data) => {
    if (!data.Next) return

    const res = await cortexGet(`${assistantUrl.split('/v')[0]}${data.Next}`, accessToken)
    if (res.status !== 200) return
    const next = await res.json()

    data.Next = next.Next
    data.KnowledgeBases = [...data.KnowledgeBases, ...next.KnowledgeBases]
    await fetchAllPages(assistantUrl, accessToken, data)
}

const useKnowledgeBases = ({ assistantUrl, accessToken, accountcode }) => {
    const [state, setState] = useReducer(defaultReducer, {
        loading: true,
        knowledgeBases: {},
        selectedBase: '',
        fileConversation: false,
    })

    const refresh = useCallback(async () => {
        if (!assistantUrl || !accessToken || !accountcode) return

        setState({ loading: true })

        const [kbRes, filesRes] = await Promise.all([
            cortexGet(`${assistantUrl}/ai/knowledge_bases/?accountcodeId=${accountcode}`, accessToken),
            cortexGet(`${assistantUrl}/accountcodes/${accountcode}/files?docType=TABULAR`, accessToken),
        ])

        let kbs = []
        if (kbRes.status === 200) {
            const data = await kbRes.json()
            await fetchAllPages(assistantUrl, accessToken, data)
            kbs = data['KnowledgeBases']
                .filter((each) => each.versions.active_release)
                .filter((each) => each.versions.embedding_ver !== 0)
                .map((item) => ([item.id, { ...item, type: 'kb' }]))
        }

        let files = []
        if (filesRes.status === 200) {
            const data = await filesRes.json()
            files = data.map((each) => ([each.id, {
                id: each.id,
                name: each.name,
                parsed_name: each.name,
                type: 'file',
            }]))
        }

        const fullList = [...kbs, ...files]
        if (fullList.length === 0) {
            setState({ loading: false, knowledgeBases: {} })
            return
        }

        const knowledgeBases = Object.fromEntries(fullList)
        let defaultKb = localStorage.getItem(DEFAULT_KB_STORAGE_KEY)
        if (!defaultKb || !knowledgeBases[defaultKb])
            defaultKb = Object.keys(knowledgeBases)[0]

        setState({
            loading: false,
            knowledgeBases,
            selectedBase: defaultKb,
        })
    }, [assistantUrl, accessToken, accountcode])

    useEffect(() => { refresh() }, [refresh])

    const selectBase = useCallback((id, isFile = false) => {
        setState({ selectedBase: id, fileConversation: isFile })
        localStorage.setItem(DEFAULT_KB_STORAGE_KEY, id)
    }, [])

    return {
        loading: state.loading,
        knowledgeBases: state.knowledgeBases,
        selectedBase: state.selectedBase,
        fileConversation: state.fileConversation,
        selectBase,
        refresh,
    }
}

export default useKnowledgeBases
